import { getMetafieldGraphQLQuery } from "../../graphql/globalAndProductMetafields.gql";
import { GRAPHQL_API_ENDPOINT } from "../../utils/constants";
import {
  MakeGraphQLQuery,
  setProductAbsorbencyDropRating,
  toSnakeCase,
} from "../../utils/helpers";
import { AddToSamplesButton } from "../product-samples/add-to-samples-button-component";
import { getSampleButtonText, shouldDisabledSampleButton } from "../product-samples/product-samples";
import { AddToCartButton } from "./add-to-cart-button-component";
import { fetchStockByState, isAvailableForState, STATE_TO_LOCATION_LABEL, LABEL_TO_STATE, ALL_DISPLAY_STATES, PROVINCE_TO_STATES } from "../../utils/stock-availability";
import { GlobalState } from "../../global-state/global-state";
import { STATE_EVENTS } from "../../utils/state-events";
import { ColourSwatchComponent } from "./colour-swatch-component";
import { KlaviyoBisButton } from "./klaviyo-bis-button-component";
import QuickShopClass from "./product-quickshop";
import { QtySelectComponent } from "./qty-pack-of-component";
import { SizeLengthSelectComponent } from "./size-length-component";

const checkEveryPropertyHasValue = (metafieldGroup) => {
  if (!metafieldGroup || typeof metafieldGroup !== 'object') {
    return false;
  }
  const hasValue = (item) => item && item.value;
  return Object.values(metafieldGroup).every(hasValue);
};

export const QuickShopSideBarComponent = {
  template: "#quickshop-sidebar",
  name: "sidebar-component",
  components: {
    "quickshop-qty-dropdown": QtySelectComponent,
    "quickshop-size-length-dropdown": SizeLengthSelectComponent,
    "quickshop-colour-swatches": ColourSwatchComponent,
    "quickshop-add-to-cart-button": AddToCartButton,
    "klaviyo-bis-button": KlaviyoBisButton,
    "add-to-samples-button": AddToSamplesButton,
  },
  props: ["title", "product", "isActive", "xsellOptions"],
  mounted() {
    const scrollableContent = document.querySelector(".quickshop__content");
    if (scrollableContent) {
      scrollableContent.addEventListener("scroll", this.handleScroll);
    }
    if (this.product.options) this.updateAvailability();
  },
  beforeDestroyed() {
    const scrollableContent = document.querySelector(".quickshop__content");
    if (scrollableContent) {
      scrollableContent.removeEventListener("scroll", this.handleScroll);
    }
  },
  data() {
    return {
      qty: "1",
      selectedVariantID: "",
      selectedColourOption: null,
      selectedSizeLengthOption: null,
      selectedQtyOption: null,
      isSample: false,
      sampleData: {
        sampleKit: null,
        sampleKitWeight: null,
        sampleHandle: null,
        sampleVariantId: null,
        sampleVariantIds: null,
      },
      colourOptions: {
        label: "",
        position: null,
        values: [],
        swatches: [],
      },
      sizeLengthOptions: {
        label: "",
        position: null,
        values: [],
      },
      qtyOptions: {
        label: "",
        position: null,
        values: [],
      },
      currentVariant: {},
      discountBannerMessage: {
        discount_banner_text_colour: "",
        discount_banner_colour: "",
        discount_banner_message: null,
      },
      bSubWidget: false,
      hasAvailableQtyOptions: true,
      stockByStateData: null,
      stockDetailsOpen: false,
      effectiveState: GlobalState.getEffectiveState() || '',
    };
  },
  methods: {
    toggleStockDetails() {
      this.stockDetailsOpen = !this.stockDetailsOpen;
    },
    selectZoneState(stateCode) {
      if (!stateCode) return;
      // Dispatch to the state-selector via the event contract instead of
      // walking Vue internals. See state-selector.js for the listener.
      window.dispatchEvent(new CustomEvent(STATE_EVENTS.SELECT, {
        detail: { state: stateCode },
      }));
    },
    handleScroll() {
      const quickshopHeader = document.querySelector(".quickshop__header");
      const scrollableContent = document.querySelector(".quickshop__content");
      if (scrollableContent) {
        const verticalScrollDistance = scrollableContent.scrollTop;
        if (verticalScrollDistance > 10) {
          quickshopHeader.classList.add("scrolled");
        } else {
          quickshopHeader.classList.remove("scrolled");
        }
      }
    },
    toggleQuickShopOpen: function (isSample = false, sampleData = null) {
      // We have the ability to access the Parent Vue Instance
      // By using $parent. From there we can access the Parent methods.
      this.isSample = isSample;
      this.sampleData = sampleData;
      this.$parent.toggleQuickShopOpen();
    },
    closeQuickShop: function () {
      this.$parent.closeQuickShop();
    },
    renderDropletRating: function (tags) {
      return setProductAbsorbencyDropRating(tags);
    },
    setFirstAvailableVariant(product) {
      const firstVariant =
        QuickShopClass.getInstance().getFirstAvailableVariant(product);
      // console.log('setFirstAvailableVariant firstVariant', firstVariant, this.selectedSizeLengthOption);
      if (firstVariant) {
        const colourIndex = product.options.findIndex((option) => option.name === "Colour");
        const sizeLengthIndex = product.options.findIndex((option) => option.name === "Size" || option.name === "Length");
        const qtyIndex = product.options.findIndex((option) => option.name === "Qty");
        // console.log('setFirstAvailableVariant', colourIndex, sizeLengthIndex, qtyIndex);
        this.productHandle = this.currentVariant = firstVariant;
        this.selectedVariantID = firstVariant.id;
        if (colourIndex > -1) {
          this.selectedColourOption = firstVariant[`option${colourIndex + 1}`];
        }
        if (sizeLengthIndex > -1) {
          // #679 -- Stephanie's 19 May QA: Size+Qty products (Men's Active Fit
          // Navy, ProSkin Pants/Slip Plus/Super/Maxi, Flex) regressed because
          // updateAvailability's "all options empty" fallback re-calls
          // setFirstAvailableVariant and re-stamps firstVariant's size after
          // setSizeLengthOptions cleared it. Mirror the setSizeLengthOptions
          // guard so multi-size products stay unselected here too.
          const sizeOption = product.options[sizeLengthIndex];
          this.selectedSizeLengthOption =
            sizeOption.values.length === 1
              ? firstVariant[`option${sizeLengthIndex + 1}`]
              : "";
        }
        if (qtyIndex > -1) {
          this.selectedQtyOption = firstVariant[`option${qtyIndex + 1}`];
        }
        setTimeout(() => {  
          this.updateSampleButton();
        }, 1000);
      }
      // console.log('setFirstAvailableVariant', this.selectedSizeLengthOption);
    },
    setCurrentVariant(variant) {
      if (variant) {
        this.currentVariant = variant;
        this.selectedVariantID = variant.id;
      }
    },
    setColourOptions(product) {
      if (!product) {
        return;
      }
      const swatches = [];
      const colourOption = product.options.find(
        (option) => option.name === "Colour"
      );
      if (!colourOption) {
        this.colourOptions = {
          title: "",
          position: "",
          values: [],
          swatches: [],
        };
        return this.colourOptions;
      }

      function findColourValueMetafield(colourValueHandle) {
        // if product.colourSwatchMetafields is not set, not to pass a type and value thus template fall back to default select dropdown
        if (product.colourSwatchMetafields.length < 1) {
          return {};
        } 
        
        // in case of colour string values
        let swatchColourObject = [];

        const swatchColourValues = JSON.parse(
          product.colourSwatchMetafields.find(
            (metafield) => metafield.key === "swatch_colour"
          )?.value
        ); // ['Noir [#312B27]', 'Beige [#F5F5DC]', 'Dusty Pink [#D58A94]']

        if (swatchColourValues) {
          // destruct to get Colour name and hex value and return an object
          swatchColourObject = swatchColourValues.map((colour) => {
            const [colourName, colourHex] = colour.split("[").map((item) =>
              item
                .replace("]", "")
                // also remove head and tail space if any
                .trim()
            );
            return { colour: colourName, code: colourHex };
          }); // [{"colour":"Noir","code":"#312B27"},{"colour":"Beige","code":"#F5F5DC"},{"colour":"Dusty Pink","code":"#D58A94"}]
        }

        // in case of file images
        let swatchMediaObject = [];

        const swatchMediaReferences = product.colourSwatchMetafields.find(
          (metafield) => metafield.key === "swatch_media"
        )?.references; // {"edges":[{"node":{"image":{"url":"https://cdn.shopify.com/s/files/1/0284/6405/4346/files/swatch_media_Noir.png?v=1681894864"}}}

        if (swatchMediaReferences) {
          const swatchMediaUrls = swatchMediaReferences.edges.map(
            (edge) => edge.node.image.url
          ); //https://cdn.shopify.com/s/files/1/0284/6405/4346/files/swatch_media_Dusty-Pink.png?v=1681908925
  
          // if not empty, get colour name from file name and return an object
          swatchMediaObject = swatchMediaUrls.map((url) => {
            const colourName = url
              .split("/")
              .pop()
              .split(".")[0]
              .replace("swatch_media_", "")
              .replace(/[_-]]/g, " "); // swatch_media_Dusty-Pink.png => Dusty Pink
            return { colour: colourName, url: url };
          }); // [{"colour":"Noir","url":"https://cdn.shopify.com/s/files/1/0284/6405/4346/files/swatch_media_Noir.png?v=1681894864"}]
        }

        const colourValueHandleCode = swatchColourObject.find(
          (colour) => toSnakeCase(colour.colour) === colourValueHandle
        );

        const colourValueHandleImage = swatchMediaObject.find(
          (colour) => toSnakeCase(colour.colour) === colourValueHandle
        );

        if (colourValueHandleImage != undefined) {
          return {
            type: "image",
            value: colourValueHandleImage?.url || "#",
          };
        }

        if (colourValueHandleCode != undefined) {
          return {
            type: "code",
            value: colourValueHandleCode?.code || "",
          };
        }
      }

      colourOption.values.forEach((colourValue) => {
        const colourValueHandle = toSnakeCase(colourValue);
        const typeAndValue = findColourValueMetafield(colourValueHandle);

        swatches.push({
          name: colourValue,
          handle: colourValueHandle,
          ...typeAndValue,
        });
      });
      this.colourOptions = {
        title: colourOption ? colourOption.name : "",
        position: colourOption ? colourOption.position : "",
        values: colourOption ? colourOption.values : [],
        swatches: colourOption ? swatches : [],
      };
      // console.log('colourOptions', this.colourOptions);
      $("select.custom-styled-dropdown").niceSelect();
    },
    setSizeLengthOptions(product) {
      const sizeLengthOptions = product.options.find(
        (option) => option.name === "Size" || option.name === "Length"
      );
      if (!sizeLengthOptions) {
        this.sizeLengthOptions = {
          title: "",
          position: "",
          values: [],
        };
        return this.colourOptions;
      }
      this.sizeLengthOptions = {
        title: sizeLengthOptions ? sizeLengthOptions.name : "",
        position: sizeLengthOptions ? sizeLengthOptions.position : "",
        values: sizeLengthOptions ? sizeLengthOptions.values : [],
      };
      // #679 -- Only auto-select if single size; leave unselected for multi-size
      if (sizeLengthOptions) {
        this.selectedSizeLengthOption = sizeLengthOptions.values.length === 1
          ? sizeLengthOptions.values[0]
          : '';
      }
      $("select.custom-styled-dropdown").niceSelect();
    },
    getAvailableQtyOptions(product) {
      // console.log('getAvailableQtyOptions', JSON.stringify(product.options));
      const qtyIndex = product.options.findIndex((option) => option.name === "Qty");
      const sizeIndex = product.options.findIndex((option) => option.name === "Size" || option.name === "Length");
      const colourIndex = product.options.findIndex((option) => option.name === "Colour");
      let avaiableQtyOptions = product.variants;
      if (sizeIndex > -1) {
        if (colourIndex > -1) { // 3 options
          avaiableQtyOptions = product.variants.filter(
            (variant) => variant.options[sizeIndex] === this.selectedSizeLengthOption && variant.options[colourIndex] === this.selectedColourOption
          );
        } else { // 2 options
          avaiableQtyOptions = product.variants.filter(
            (variant) => variant.options[sizeIndex] === this.selectedSizeLengthOption
          );
        }
      }

      return { qtyIndex, avaiableQtyOptions}
    },
    setQtyOptions(product) {
      const { qtyIndex, avaiableQtyOptions } = this.getAvailableQtyOptions(product);
      
      // Filter out "Samples Only" from the values
      const filteredValues = avaiableQtyOptions
        .map((variant) => variant.options[qtyIndex])
        .filter(Boolean)
        .filter(value => value !== 'Samples Only');
      
      // Get the corresponding variant IDs and prices for filtered values
      const filteredVariants = avaiableQtyOptions.filter(variant => 
        variant.options[qtyIndex] !== 'Samples Only'
      );
      
      this.qtyOptions = {
        title: 'Qty',
        position: qtyIndex + 1,
        values: filteredValues,
        variantIds: filteredVariants.map((variant) => variant.id),
        variantPrices: filteredVariants.map((variant) =>
          QuickShopClass.getInstance().renderPrice(variant.price)
        ),
        handle: product.handle,
      };
      // console.log('setQtyOptions', this.qtyOptions);
      // If no quantity options are available, reset the selected quantity
      if (this.qtyOptions.values.length === 0) {
        this.selectedQtyOption = null;
      } else if (!this.qtyOptions.values.includes(this.selectedQtyOption)) {
        // If the current selected quantity is not in the available options, select the first available one
        this.selectedQtyOption = this.qtyOptions.values[0];
      }

      this.updateAvailability();
    },
    async setDiscountMessage(handle) {
      if (!handle) {
        return;
      }
      const query = getMetafieldGraphQLQuery(handle);
      this.unsetDiscountMessage();

      try {
        const response = await MakeGraphQLQuery(GRAPHQL_API_ENDPOINT, query);
        const productMetafields = response?.data?.product;
        const globalMetafields = response?.data?.shop;
        const metafields = { productMetafields, globalMetafields };
        const discountBannerMetafield =
          this.getDiscountMessageBadgeUiMetafields(metafields);

        this.setDiscountMessageBadgeUI(discountBannerMetafield);
      } catch (error) {
        console.error("Error:", error);
      }
    },
    setSelectedQtyOption(qty) {
      this.selectedQtyOption = qty;
      this.updateAvailability();
      this.updateSampleButton();
    },
    changeSelect(event) {
      setTimeout(() => {
        this.selectedColourOption = event;
        this.updateAvailability();
        this.setQtyOptions(this.product);
        this.updateSampleButton();
      }, 250);
    },
    changeSizeLengthSelect(event) {
      setTimeout(() => {
        this.selectedSizeLengthOption = event;
        // console.log('changeSizeLengthSelect', event);
        this.updateAvailability();
        this.setQtyOptions(this.product);
        this.updateSampleButton();
      }, 250);
    },
    updateSampleButton() {
      if (this.isSample && this.sampleData?.sampleVariantId) {
        // console.log('updateSampleButton', this.isSample, this.sampleData, this.currentVariant, this.selectedVariantID)
    //     const text = getSampleButtonText(`${this.selectedVariantID}`, this.sampleData.sampleKit, this.sampleData.sampleKitWeight);
    //     const disabled = shouldDisabledSampleButton(text);
    //     console.log('updateSampleButton', text, disabled);
        const button = document.querySelector(`[data-add-to-samples='${this.sampleData.sampleHandle}']`);
        // console.log('updateSampleButton button', button)
        if (button) {
          button.dataset.selectedVariantId = `${this.selectedVariantID}`;
          const text = getSampleButtonText(`${this.selectedVariantID}`, this.sampleData.sampleKit, this.sampleData.sampleKitWeight);
          const disabled = shouldDisabledSampleButton(text);
          // console.log('updateSampleButton', text, disabled);
          button.firstElementChild.innerHTML = text;
          button.classList.toggle('cta-button--disabled', disabled);
          button.classList.toggle('cta-button--grey', disabled);
          button.classList.toggle('cta-button--green', !disabled);
        }
      }
    },
    onOptionChange(productJSON, selectedOptions) {
      // console.log('onOptionChange', productJSON, selectedOptions);
      return QuickShopClass.getInstance().onOptionChange(
        productJSON,
        selectedOptions
      );
    },
    updateQtyOptionFromSuggestedVariant(variant) {
      if (variant.options.length === 3) {
        this.selectedColourOption = variant.option1;
        this.selectedSizeLengthOption = variant.option2;
        this.selectedQtyOption = variant.option3;
      } else if (variant.options.length === 2) {
        this.selectedColourOption = null;
        this.selectedSizeLengthOption = variant.option1;
        this.selectedQtyOption = variant.option2;
      } else {
        this.selectedColourOption = null;
        this.selectedSizeLengthOption = null;
        this.selectedQtyOption = variant.option1;
      }
      // console.log('updateQtyOptionFromSuggestedVariant', this.selectedSizeLengthOption);
    },
    unsetDiscountMessage() {
      // Reset the discount banner
      this.discountBannerMessage.discount_banner_text_colour = "";
      this.discountBannerMessage.discount_banner_colour = "";
      this.discountBannerMessage.discount_banner_message = null;
    },
    getDiscountMessageBadgeUiMetafields({
      globalMetafields,
      productMetafields,
    }) {
      const hasProductDiscountBadge = productMetafields &&
        checkEveryPropertyHasValue(productMetafields);
      const hasGlobalDiscountBadge = globalMetafields &&
        checkEveryPropertyHasValue(globalMetafields);
      return hasProductDiscountBadge
        ? productMetafields
        : hasGlobalDiscountBadge
        ? globalMetafields
        : null;
    },
    setDiscountMessageBadgeUI(discountBannerMetafields) {
      if (!discountBannerMetafields) {
        return;
      }
      Object.entries(discountBannerMetafields).forEach((metafield) => {
        const metafieldKey = metafield[0];
        const { value } = metafield[1];
        if (metafieldKey && value) {
          this.discountBannerMessage[metafieldKey] = value;
        }
      });
    },
    setBSubWidget(product) {
      // if (this.bSubWidget) {
      //   return this.bSubWidget;
      // }

      if (product?.url) {
        const headers = { "X-XSS-Protection": 0 };
        const url = product?.url + "?section_id=api-quickview-bold";

        fetch(url, { headers })
          .then((response) => response.text())
          .then((text) => {
            this.bSubWidget = text;
          });
      }
    },
    setBSubWidgetSelectWatcher() {
      var selectElement = document.querySelector("#quickShopSubscriptions");
      var hiddenField = document.querySelector(
        "#quickShopSubscriptionsSellingPlan"
      );

      if (selectElement && hiddenField) {
        jQuery(selectElement).niceSelect();

        selectElement.addEventListener("change", (event) => {
          var currentSellingPlanId = event.target.value;
          hiddenField.value = currentSellingPlanId;
        });
      }
    },
    findVariant(options) {
      if (!this.product.options) return [];
      // Get the position of each option type
      const optionPositions = {};
      this.product.options.forEach((option, index) => {
        if (option.name === "Size" || option.name === "Length") {
          optionPositions.size = `option${index + 1}`;
        } else {
          optionPositions[option.name] = `option${index + 1}`;
        }
      });

      return this.product.variants.find(variant => 
        // Check each option we're looking for
        (!options.colour || !optionPositions['Colour'] || variant[optionPositions['Colour']] === options.colour) &&
        (!options.size || !optionPositions.size || variant[optionPositions.size] === options.size) &&
        (!options.qty || !optionPositions['Qty'] || variant[optionPositions['Qty']] === options.qty)
      );
    },
    updateAvailability() {
      // Add safety check but don't return early - set defaults instead
      if (!this.product || !this.product.options || !this.product.variants) {
        this.hasAvailableQtyOptions = false;
        return;
      }

      // If no options are selected yet, set defaults from first available variant
      if (!this.selectedColourOption && !this.selectedSizeLengthOption && !this.selectedQtyOption) {
        this.setFirstAvailableVariant(this.product);
        return; // setFirstAvailableVariant will trigger another updateAvailability call
      }

      const options = {
        colour: this.selectedColourOption,
        size: this.selectedSizeLengthOption,
        qty: this.selectedQtyOption
      };

      const variant = this.findVariant(options);
      // this.isAvailable = !!variant && variant.available;

      // Update current variant if a valid one is found
      if (variant) {
        this.setCurrentVariant(variant);
      }
      // console.log('updateAvailability', this.product, options, variant);
      // Check if there are any available quantity options
      const availableQtyOptions = this.getAvailableQtyOptions(this.product);
      this.hasAvailableQtyOptions = availableQtyOptions.avaiableQtyOptions.length > 0;
    },
    handleSampleAdded() {
      this.closeQuickShop();
    },
    handleCartAdded() {
      this.closeQuickShop();
    },
  },

  // {
  //   handle: "blanc",
  //   img: "https://cdn.shopify.com/s/files/1/0374/1941/7644/products/swatch_Blanc_af7b451b-d9fb-4bbb-abf6-928ed7e94cb7.png?v=1599458203",
  //   name: "Blanc",
  // }

  // {
  //       "namespace": "custom",
  //       "key": "colour_noir_colour_code",
  //       "value": "#333"
  //   },

  filters: {
    setColourSwatchName: function (metafield) {
      let name;
      if (image.alt !== "") {
        name = image.alt.replace("swatch_", "").replace("-", " ");
      } else {
        name = image.src.split("/swatch_")[1];
        name = name
          .replace("swatch_", "")
          .replace(".jpg", "")
          .replace(".png", "")
          .replace(/\?.*$/g, "")
          .replace("-", " ");
      }
      return name;
    },
    setColourSwatchHandle: function (image) {
      let handle;
      if (image.alt !== "") {
        handle = image.alt.replace("swatch_", "").replace("-", " ");
      } else {
        handle = image.src.split("/swatch_")[1];
        handle = handle
          .replace("swatch_", "")
          .replace(".jpg", "")
          .replace(".png", "")
          .replace(/\?.*$/g, "")
          .replace("-", " ");
      }
      return handle.toLowerCase();
    },
    formatMoney: function (value) {
      if (!value) return false;
      return QuickShopClass.getInstance().renderPrice(value);
    },
  },
  computed: {
    price: function () {
      return this.currentVariant
        ? this.$options.filters.formatMoney(this.currentVariant.price)
        : "";
    },
    compareAtPrice: function () {
      return this.currentVariant
        ? this.$options.filters.formatMoney(
            this.currentVariant.compare_at_price
          )
        : null;
    },
    totalPrice: function () {
      const amount = this.qty * this.currentVariant.price;
      return this.$options.filters.formatMoney(amount);
    },
    totalCompareAtPrice: function () {
      const amount = this.qty * this.currentVariant.compare_at_price;
      return this.$options.filters.formatMoney(amount);
    },
    onSale: function () {
      return this.currentVariant &&
        this.currentVariant.compare_at_price &&
        this.currentVariant.compare_at_price > this.currentVariant.price
        ? true
        : false;
    },
    selectedOptions: function () {
      let selectedOptionArray = [];
      if (this.product.options.length === 3) {
        selectedOptionArray.push(
          this.selectedColourOption,
          this.selectedSizeLengthOption,
          this.selectedQtyOption
        );
      } else if (this.product.options.length === 2) {
        if (
          this.selectedSizeLengthOption.length &&
          this.selectedQtyOption.length
        ) {
          selectedOptionArray.push(
            this.selectedSizeLengthOption,
            this.selectedQtyOption
          );
        } else {
          selectedOptionArray.push(this.selectedQtyOption);
        }
      }
      // console.log('selectedOptions', selectedOptionArray);
      return selectedOptionArray;
    },
    isAvailable: function () {
      return this.currentVariant && this.currentVariant.available;
    },
    isStateAvailable: function () {
      if (!this.stockByStateData || !this.stockByStateData.variants) return null;
      if (!this.effectiveState) return null;
      var variantId = this.selectedVariantID;
      if (!variantId) return null;
      return isAvailableForState(this.stockByStateData, variantId, this.effectiveState);
    },
    stateLocationLabel: function () {
      return this.effectiveState ? (STATE_TO_LOCATION_LABEL[this.effectiveState] || this.effectiveState) : '';
    },
    stockZones: function () {
      if (!this.stockByStateData || !this.stockByStateData.variants) return [];
      var variantId = this.selectedVariantID;
      var stockData = this.stockByStateData;
      return ALL_DISPLAY_STATES.map(function (label) {
        var stateCode = LABEL_TO_STATE[label];
        var states = PROVINCE_TO_STATES[stateCode] || [stateCode];
        var available = states.some(function (s) { return isAvailableForState(stockData, variantId, s) === true; });
        return { label: label, available: available, stateCode: stateCode };
      });
    },
    // #679 -- True when multi-size product has no explicit size picked
    sizeNotSelected() {
      return this.sizeLengthOptions?.values?.length > 1 && !this.selectedSizeLengthOption;
    },
    isValidVariant() {
      if (!this.isAvailable || !this.hasAvailableQtyOptions) return false;
      // #679 -- block ATC when an explicit size pick is required but missing
      if (this.sizeNotSelected) return false;
      // #417 -- if state-level stock data exists, check it too
      var stateAvail = this.isStateAvailable;
      if (stateAvail === false) return false;
      return true;
    },
  },
  watch: {
    product: function (newProduct) {
      this.setBSubWidget(newProduct);
      this.updateAvailability();
      // Reset stock-by-state state for new product
      this.stockByStateData = null;
      this.stockDetailsOpen = false;
      if (newProduct && newProduct.handle) {
        fetchStockByState(newProduct.handle).then((data) => {
          this.stockByStateData = data;
        });
      }
    },
  },
  created() {
    const self = this;
    const syncState = () => { self.effectiveState = GlobalState.getEffectiveState() || ''; };
    window.addEventListener(STATE_EVENTS.SELECTED, syncState);
    window.addEventListener(STATE_EVENTS.CLEARED, syncState);
    window.addEventListener(STATE_EVENTS.DETECTED, syncState);
  },
  updated: function () {
    this.setBSubWidgetSelectWatcher();
  },
};

